import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Link } from 'react-router-dom';
import { Users, Sparkle, Lightning, Heart, ShieldCheck, ChartLineUp, HandCoins, Plus, Star } from '@phosphor-icons/react';

const Tile = ({ icon: Icon, label, value, accent }) => (
  <div className="glass rounded-2xl p-5">
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/65">
      <Icon size={14} weight="bold" /> {label}
    </div>
    <div className="font-display text-4xl font-black mt-2" style={{ color: accent }}>{Number(value || 0).toLocaleString()}</div>
  </div>
);

const Admin = () => {
  const [m, setM] = useState(null);
  const [users, setUsers] = useState([]);
  const [verify, setVerify] = useState(null);
  const [payouts, setPayouts] = useState({ total_collected: 0, total_distributed_owed: 0, by_cause: [], payouts: [] });
  const [form, setForm] = useState({ cause_id: '', amount: '', reference: '', note: '' });

  const loadPayouts = async () => {
    const { data } = await api.get('/admin/payouts/summary');
    setPayouts(data);
  };

  useEffect(() => {
    api.get('/admin/metrics').then(({ data }) => setM(data));
    api.get('/admin/users?limit=20').then(({ data }) => setUsers(data.users));
    api.get('/ledger/verify').then(({ data }) => setVerify(data));
    loadPayouts();
  }, []);

  const recordPayout = async () => {
    if (!form.cause_id || !form.amount) { toast.error('cause + amount required'); return; }
    try {
      await api.post('/admin/payouts', { ...form, amount: parseFloat(form.amount) });
      toast.success('Payout logged on ledger');
      setForm({ cause_id: '', amount: '', reference: '', note: '' });
      loadPayouts();
    } catch (err) { toast.error(err?.response?.data?.detail || 'failed'); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 pb-24">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <ShieldCheck size={24} weight="duotone" className="text-bx-cyan" />
          <h1 className="font-display text-4xl font-black tracking-tighter">Control Room</h1>
        </div>
        <Link to="/admin/curator" data-testid="admin-to-curator" className="btn-volt px-5 py-2.5 rounded-full text-sm flex items-center gap-1.5">
          <Star size={14} weight="fill" /> Celebrity Curator
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Tile icon={Users} label="Founders" value={m?.users} accent="#00f0ff" />
        <Tile icon={Sparkle} label="Bubbles" value={m?.bubbles} accent="#ff007f" />
        <Tile icon={Lightning} label="Transfers" value={m?.transfers} accent="#ccff00" />
        <Tile icon={ChartLineUp} label="Posts" value={m?.posts} accent="#ffd700" />
        <Tile icon={Users} label="Tribes" value={m?.tribes} accent="#00ffc8" />
        <Tile icon={Heart} label="Donations" value={m?.donations} accent="#ff5f00" />
        <Tile icon={Lightning} label="Energy Donated" value={m?.energy_donated} accent="#ccff00" />
        <Tile icon={ShieldCheck} label="Ledger" value={verify?.entries} accent={verify?.valid ? '#00f0ff' : '#ff007f'} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <div className="glass rounded-2xl p-5">
          <h2 className="font-display text-xl font-bold mb-3">Recent founders</h2>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {(m?.recent_users || []).map((u) => (
              <div key={u.id} className="flex items-center gap-3 text-sm">
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-bx-cyan to-bx-magenta grid place-items-center font-display font-black text-xs">
                  {u.username?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="font-bold">@{u.username}</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/60">{u.email} · #{u.founder_number}</div>
                </div>
                <div className="text-bx-volt font-bold">{u.xp} xp</div>
              </div>
            ))}
          </div>
        </div>

        <div className="glass rounded-2xl p-5">
          <h2 className="font-display text-xl font-bold mb-3">Ledger integrity</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-white/55">Status</span>
              <span className={verify?.valid ? 'text-bx-cyan font-bold' : 'text-bx-magenta font-bold'}>
                {verify?.valid ? 'VERIFIED ✓' : 'TAMPERED ✗'}
              </span>
            </div>
            <div className="flex justify-between"><span className="text-white/55">Entries</span><span className="font-mono">{verify?.entries}</span></div>
            <div><span className="text-white/55">Tip hash</span>
              <code className="block mt-1 font-mono text-[10px] text-white/70 break-all">{verify?.tip_hash}</code>
            </div>
          </div>
        </div>
      </div>

      <div className="glass rounded-2xl p-5 mt-6">
        <div className="flex items-center gap-2 mb-4">
          <HandCoins size={20} weight="duotone" className="text-bx-volt" />
          <h2 className="font-display text-xl font-bold">Cause Distribution Payouts</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          <div className="glass-hi rounded-xl p-4">
            <div className="text-[10px] uppercase tracking-widest text-white/65">Total collected</div>
            <div className="font-display text-2xl font-black text-bx-cyan mt-1">${payouts.total_collected.toFixed(2)}</div>
          </div>
          <div className="glass-hi rounded-xl p-4">
            <div className="text-[10px] uppercase tracking-widest text-white/65">Owed to causes</div>
            <div className="font-display text-2xl font-black text-bx-magenta mt-1">${payouts.total_distributed_owed.toFixed(2)}</div>
          </div>
          <div className="glass-hi rounded-xl p-4">
            <div className="text-[10px] uppercase tracking-widest text-white/65">Payouts logged</div>
            <div className="font-display text-2xl font-black text-bx-volt mt-1">{payouts.payouts.length}</div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-white/65">
              <tr className="border-b border-white/10">
                <th className="text-left py-2">Cause</th>
                <th className="text-left py-2">Tx</th>
                <th className="text-right py-2">Owed</th>
                <th className="text-right py-2">Paid</th>
                <th className="text-right py-2">Balance due</th>
              </tr>
            </thead>
            <tbody>
              {payouts.by_cause.length === 0 && (
                <tr><td colSpan={5} className="text-center text-white/60 py-4">No paid drops yet — once a Drop is purchased, the cause split appears here.</td></tr>
              )}
              {payouts.by_cause.map((c) => (
                <tr key={c.cause_id} className="border-b border-white/5" data-testid={`payout-row-${c.cause_id}`}>
                  <td className="py-2 font-bold">{c.cause_name}</td>
                  <td className="py-2 text-white/60">{c.transactions}</td>
                  <td className="py-2 text-right text-bx-volt">${c.total_owed.toFixed(2)}</td>
                  <td className="py-2 text-right text-white/60">${c.total_paid.toFixed(2)}</td>
                  <td className="py-2 text-right font-bold text-bx-magenta">${c.balance_due.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 pt-6 border-t border-white/5">
          <h3 className="text-sm font-bold uppercase tracking-widest text-white/60 mb-3">Record a payout</h3>
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
            <select data-testid="payout-cause" value={form.cause_id} onChange={(e) => setForm({ ...form, cause_id: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm">
              <option value="">Cause…</option>
              {payouts.by_cause.map((c) => (
                <option key={c.cause_id} value={c.cause_id}>{c.cause_name}</option>
              ))}
            </select>
            <input data-testid="payout-amount" type="number" step="0.01" placeholder="USD amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
            <input data-testid="payout-ref" placeholder="Reference (wire/check #)" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
            <input data-testid="payout-note" placeholder="Note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
            <button data-testid="payout-submit" onClick={recordPayout} className="btn-primary px-4 py-2 rounded-full text-sm flex items-center gap-1 justify-center">
              <Plus size={12} weight="bold" /> Log payout
            </button>
          </div>
        </div>

        {payouts.payouts.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-bold uppercase tracking-widest text-white/60 mb-3">Payout history</h3>
            <div className="space-y-2">
              {payouts.payouts.slice().reverse().map((p) => (
                <div key={p.id} className="flex items-center justify-between bg-white/5 border border-white/10 rounded-xl p-3 text-sm">
                  <div>
                    <div className="font-bold">{p.cause_name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-white/60">{p.reference || '—'} · {new Date(p.ts).toLocaleDateString()}</div>
                  </div>
                  <div className="font-display text-xl font-black text-bx-volt">${p.amount.toFixed(2)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="glass rounded-2xl p-5 mt-6">
        <h2 className="font-display text-xl font-bold mb-3">All members</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-white/65">
              <tr className="border-b border-white/10">
                <th className="text-left py-2">@</th>
                <th className="text-left py-2">Email</th>
                <th className="text-left py-2">Founder #</th>
                <th className="text-left py-2">L</th>
                <th className="text-left py-2">XP</th>
                <th className="text-left py-2">Energy</th>
                <th className="text-left py-2">Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-white/5">
                  <td className="py-2 font-bold">@{u.username}</td>
                  <td className="py-2 text-white/60">{u.email}</td>
                  <td className="py-2 text-bx-gold">#{u.founder_number}</td>
                  <td className="py-2">{u.level}</td>
                  <td className="py-2 text-bx-volt">{u.xp}</td>
                  <td className="py-2 text-bx-cyan">{u.energy}</td>
                  <td className="py-2 text-white/60 text-xs">{u.created_at?.split('T')[0]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Admin;
